import { Component, OnInit } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { UserApiService } from '../services/user-api.service';
import { OrganizationStateService } from 'src/app/shared/organization-state.service';

@Component({
  selector: 'app-show-user',
  templateUrl: './show-user.component.html',
  styleUrls: ['./show-user.component.scss'],
  standalone: true,
  imports: [CommonModule, MatIconModule],
})
export class ShowUserComponent implements OnInit {
  route: ActivatedRoute;
  router: Router;
  users: any[] = [];
  showSuccess = false;
  orgId: string = '';
  orgName: string = '';

  // Pagination
  pageSize = 5;
  currentPage = 1;
  get totalPages() {
    return Math.ceil(this.users.length / this.pageSize);
  }
  get pagedUsers() {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.users.slice(start, start + this.pageSize);
  }

  constructor(
    router: Router,
    route: ActivatedRoute,
    private readonly userApi: UserApiService,
    private orgState: OrganizationStateService // <-- Inject here
  ) {
    this.router = router;
    this.route = route;
    this.route.queryParamMap.subscribe((params) => {
      if (params.get('updated') === '1') {
        this.showSuccess = true;
        setTimeout(() => {
          this.showSuccess = false;
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { updated: null },
            queryParamsHandling: 'merge',
            replaceUrl: true,
          });
        }, 1500);
      }
    });
  }

  ngOnInit() {
    this.route.paramMap.subscribe((params) => {
      const orgId = params.get('orgId');
      if (orgId) {
        this.orgId = orgId;
        // Fetch org name from state/service
        this.orgState.orgResponse$.subscribe((org) => {
          this.orgName = org?.name || '';
        });
        this.fetchUsers();
      }
    });
  }

  private mapUser(u: any): any {
    if (!u?.msgValue) return u;
    try {
      const parsed = JSON.parse(u.msgValue);
      return this.extractUserFields(parsed, u);
    } catch {
      return u;
    }
  }

  private getId(parsed: any, type: string, system: string): string {
    if (!Array.isArray(parsed.identifiers)) return '-';
    const found = parsed.identifiers.find(
      (id: any) => id.type === type && id.system === system
    );
    return found?.value || '-';
  }

  private getField(obj: any, field: string, fallback: any = '-') {
    return obj?.[field] ?? fallback;
  }

  private getEmail(parsed: any): string {
    if (!Array.isArray(parsed.telecoms)) return '-';
    const emailObj = parsed.telecoms.find((t: any) => t.system === 'Email');
    return emailObj?.value || '-';
  }

  private getExcludeReferrals(parsed: any): boolean {
    return typeof parsed.excludeReferrals === 'boolean'
      ? parsed.excludeReferrals
      : false;
  }

  private extractUserFields(parsed: any, raw: any): any {
    return {
      username: this.getField(parsed, 'userName'),
      firstname: this.getField(parsed.name, 'given'),
      lastname: this.getField(parsed.name, 'family'),
      active: typeof parsed.active === 'boolean' ? parsed.active : false,
      email: this.getEmail(parsed),
      userId: this.getId(parsed, 'UUID', 'https://philips.com/careorchestrator'),
      orgId: parsed.partOf?.identifier?.value || '-',
      excludeReferrals: this.getExcludeReferrals(parsed),
      raw,
    };
  }

  fetchUsers() {
    this.userApi.getUsersWithToken(this.orgId).subscribe({
      next: (data) => {
        const usersArr: any[] = Array.isArray(data?.matchingMsgs)
          ? data.matchingMsgs
          : [];
        const mappedUsers = usersArr.map((u: any) => this.mapUser(u));
        this.users = this.getUniqueUsersById(mappedUsers);
        this.currentPage = 1;
      },
      error: () => {
        this.users = [];
      },
    });
  }

  private getUniqueUsersById(users: any[]): any[] {
    const uniqueUsersMap = new Map();
    for (let i = users.length - 1; i >= 0; i--) {
      const user = users[i];
      if (user?.userId && !uniqueUsersMap.has(user.userId)) {
        uniqueUsersMap.set(user.userId, user);
      }
    }
    return Array.from(uniqueUsersMap.values()).reverse();
  }

  onEditUser(user: any) {
    const orgId = user.orgId || this.orgId;
    const userId = user.userId || user.id || user._id;
    console.log('Navigating to edit user with:', { orgId, userId, user });
    if (orgId && userId) {
      this.router.navigate(['/organization/edit-user', orgId, userId]);
    }
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  prevPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
    }
  }
}
